import { createContext, useContext } from 'react';
import type { User } from 'firebase/auth';
import type { Timestamp } from 'firebase/firestore';

// The context object and its hook live in a non-component module: a .tsx that
// exports both a component and a hook breaks fast refresh (react-refresh's
// only-export-components).
/** Manual PWZ review state (docs/reference/doctor-launch-plan-2026-10.md §4). */
export type PwzStatus = 'pending' | 'verified' | 'rejected';

export interface UserProfile {
  uid: string;
  email?: string;
  name?: string;
  pwz?: string | null;
  // Written only by the backend (complete_registration / review_doctor);
  // Firestore rules deny every client write to users/{uid}.
  pwz_checksum_ok?: boolean | null;
  pwz_status?: PwzStatus | null;
  pwz_submitted_at?: Timestamp | null;
  pwz_reviewed_by?: string | null;
  pwz_reviewed_at?: Timestamp | null;
  specialty?: string | null;
  workplace?: string | null;
  terms_accepted_at?: Timestamp | null;
  terms_version?: string | null;
  onboarding_completed_at?: Timestamp | null;
  marketing_opt_in?: boolean;
  analytics_opt_in?: boolean;
  analytics_consent_updated_at?: Timestamp | null;
  registration_completed_at?: Timestamp | null;
  created_at?: Timestamp | null;
}

export interface AuthContextProps {
  user: User | null;
  loading: boolean;
  profile: UserProfile | null;
  profileLoading: boolean;
  /** True when signed in but PWZ / terms gate is incomplete. */
  needsOnboarding: boolean;
  // True when the user is an admin: either via the `admin: true` custom claim
  // (set with functions/python/scripts/set_admin_claim.py — the same claim
  // Firestore rules key on) or the VITE_ADMIN_UIDS allowlist (bootstrap
  // fallback so the UI works before claims are granted).
  admin: boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  profile: null,
  profileLoading: false,
  needsOnboarding: false,
  admin: false,
});

export const useAuth = () => {
  return useContext(AuthContext);
};
